// src/components/PartsManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

const EMPTY_PART = {
  partId: "",
  name: "",
  part_code: "",
  supplier: "",
  description: "",
  dimensions: "",
  defaultQuantity: 1,
  datasheetId: "",
  imageFolderId: "",
  imageFolderUrl: "",
  imageUrl: "",
  createdAt: null,
  updatedAt: null,
};

export default function PartsManager() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | create | edit | copy

  const [form, setForm] = useState(EMPTY_PART);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  // image upload
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const spCreatePartImageFolder = useMemo(
    () => httpsCallable(functions, "spCreatePartImageFolder"),
    []
  );
  const spCreateUploadSession = useMemo(
    () => httpsCallable(functions, "spCreateUploadSession"),
    []
  );

  // Load parts
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const qParts = query(collection(db, "parts"), orderBy("name"));
        const snap = await getDocs(qParts);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setParts(rows);
      } catch (e) {
        console.error(e);
        setSaveError("Failed to load parts.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = !search.trim()
    ? parts
    : parts.filter((p) => {
        const s = search.toLowerCase();
        return (
          String(p.partId || p.id || "")
            .toLowerCase()
            .includes(s) ||
          String(p.part_code || "")
            .toLowerCase()
            .includes(s) ||
          String(p.name || "")
            .toLowerCase()
            .includes(s) ||
          String(p.supplier || "")
            .toLowerCase()
            .includes(s)
        );
      });

  function onSelect(id) {
    const p = parts.find((x) => x.id === id);
    if (!p) return;
    setSelectedId(id);
    setMode("edit");
    setForm({
      ...EMPTY_PART,
      ...p,
      partId: p.partId || p.id,
      defaultQuantity: p.defaultQuantity || 1,
    });
    setSaveError("");
    setInfoMessage("");
    setImageFile(null);
  }

  function startNew() {
    setSelectedId(null);
    setMode("create");
    setForm(EMPTY_PART);
    setSaveError("");
    setInfoMessage("");
    setImageFile(null);
  }

  function copySelected() {
    if (!selectedId) {
      setSaveError("Select a part to copy.");
      return;
    }
    const p = parts.find((x) => x.id === selectedId);
    if (!p) return;

    const copy = {
      ...EMPTY_PART,
      ...p,
      partId: "",
      part_code: "",
      name: p.name ? `${p.name} (Copy)` : "",
      imageFolderId: "",
      imageFolderUrl: "",
      imageUrl: "",
    };
    setSelectedId(null);
    setMode("copy");
    setForm(copy);
    setSaveError("");
    setInfoMessage("");
    setImageFile(null);
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function save(e) {
    e?.preventDefault();
    setSaveError("");
    setInfoMessage("");

    if (!form.partId.trim()) {
      setSaveError("Part ID is required.");
      return;
    }

    const partId = form.partId.trim().toUpperCase();
    const ref = doc(db, "parts", partId);

    const payload = {
      ...form,
      partId,
      defaultQuantity: form.defaultQuantity
        ? Number(form.defaultQuantity)
        : 1,
      updatedAt: serverTimestamp(),
    };
    if (!payload.createdAt) payload.createdAt = serverTimestamp();

    try {
      setIsSaving(true);

      await setDoc(ref, payload, { merge: true });

      setParts((prev) => {
        const idx = prev.findIndex((p) => p.id === partId);
        const row = { id: partId, ...payload };
        if (idx === -1) {
          return [...prev, row].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""))
          );
        }
        const copy = [...prev];
        copy[idx] = row;
        return copy.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      });

      setSelectedId(partId);
      setMode("edit");
      setInfoMessage("Part saved.");
    } catch (err) {
      console.error(err);
      setSaveError("Failed to save part.");
    } finally {
      setIsSaving(false);
    }
  }

  async function ensureImageFolder() {
    if (!form.partId.trim()) {
      setSaveError("Save a Part ID first, then create an image folder.");
      throw new Error("no-part-id");
    }

    if (form.imageFolderId) {
      // already linked
      return {
        folderItemId: form.imageFolderId,
        folderWebUrl: form.imageFolderUrl,
      };
    }

    const partId = form.partId.trim().toUpperCase();

    const res = await spCreatePartImageFolder({ partId });
    const { folderItemId, folderWebUrl } = res.data || {};
    if (!folderItemId || !folderWebUrl) {
      throw new Error("spCreatePartImageFolder did not return folderItemId/webUrl.");
    }

    const updated = {
      imageFolderId: folderItemId,
      imageFolderUrl: folderWebUrl,
    };

    setForm((prev) => ({ ...prev, ...updated }));

    await setDoc(
      doc(db, "parts", partId),
      {
        ...updated,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return { folderItemId, folderWebUrl };
  }

  async function uploadImage() {
    if (!imageFile) return;
    if (!form.partId.trim()) {
      setSaveError("Save a Part ID first, then upload an image.");
      return;
    }

    try {
      setUploadingImage(true);
      setSaveError("");
      setInfoMessage("");

      const partId = form.partId.trim().toUpperCase();
      const { folderItemId } = await ensureImageFolder();

      const sessionRes = await spCreateUploadSession({
        folderItemId,
        fileName: imageFile.name,
      });
      const { uploadUrl } = sessionRes.data || {};
      if (!uploadUrl) throw new Error("No uploadUrl returned.");

      const size = imageFile.size;
      const end = size - 1;

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": imageFile.type || "image/jpeg",
          "Content-Length": String(size),
          "Content-Range": `bytes 0-${end}/${size}`,
        },
        body: imageFile,
      });

      if (!putRes.ok) {
        const text = await putRes.text().catch(() => "");
        throw new Error(`Upload failed: ${putRes.status} ${text}`);
      }

      const driveItem = await putRes.json();
      const webUrl = driveItem?.webUrl;
      if (!webUrl) throw new Error("Upload succeeded but no webUrl returned.");

      const updated = {
        imageUrl: webUrl,
        imageFolderId: folderItemId,
        imageFolderUrl: form.imageFolderUrl || "", // keep existing
      };

      setForm((prev) => ({ ...prev, ...updated }));

      await setDoc(
        doc(db, "parts", partId),
        {
          ...updated,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setInfoMessage("Image uploaded and linked.");
    } catch (err) {
      console.error(err);
      if (err.message !== "no-part-id") {
        setSaveError(err.message || "Failed to upload image.");
      }
    } finally {
      setUploadingImage(false);
    }
  }

  const modeLabel =
    mode === "create"
      ? "Creating part"
      : mode === "copy"
      ? "Copying part"
      : mode === "edit"
      ? `Editing ${form.partId || selectedId || ""}`
      : "Select or create a part";

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Parts</h1>

        <button
          type="button"
          onClick={startNew}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
        >
          New part
        </button>
        <button
          type="button"
          onClick={copySelected}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
        >
          Copy selected
        </button>

        <span className="ml-auto text-sm text-gray-500">
          {isSaving
            ? "Saving…"
            : mode === "idle"
            ? "No part selected"
            : "Ready"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">
              Search parts
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
              placeholder="ID, code, name, supplier…"
            />
          </div>

          <div className="border-t border-gray-200 pt-2 max-h-[400px] overflow-auto text-sm">
            {loading ? (
              <div className="text-gray-500 text-sm p-2">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-gray-500 text-sm p-2">No parts found.</div>
            ) : (
              <ul className="space-y-1">
                {filtered.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(p.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                        selectedId === p.id
                          ? "bg-indigo-600 text-white"
                          : "hover:bg-gray-100 text-gray-800"
                      }`}
                    >
                      <div className="font-medium truncate">
                        {p.name || "Untitled part"}
                      </div>
                      <div className="text-xs text-gray-200 lg:text-gray-300">
                        {p.partId || p.id}
                        {p.part_code ? ` · ${p.part_code}` : ""}
                        {p.supplier ? ` · ${p.supplier}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div>
              <b>Mode:</b> {modeLabel}
            </div>
            <div>
              <b>Count:</b> {parts.length}
            </div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={save} className="space-y-8">
          <section className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Part details
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Unique internal ID, codes, supplier and description.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Part ID<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.partId}
                  onChange={(e) =>
                    setField("partId", e.target.value.toUpperCase())
                  }
                  placeholder="IMH-3KW-230V"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used as Firestore document ID – keep stable.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Part code (optional)
                </label>
                <input
                  type="text"
                  value={form.part_code}
                  onChange={(e) => setField("part_code", e.target.value)}
                  placeholder="Alternative / supplier code"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Immersion heater 3kW 230V"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier
                </label>
                <input
                  type="text"
                  value={form.supplier}
                  onChange={(e) => setField("supplier", e.target.value)}
                  placeholder="Telford / external supplier"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default quantity
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.defaultQuantity}
                  onChange={(e) =>
                    setField(
                      "defaultQuantity",
                      e.target.value ? Number(e.target.value) : 1
                    )
                  }
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datasheet ID (optional)
                </label>
                <input
                  type="text"
                  value={form.datasheetId}
                  onChange={(e) =>
                    setField("datasheetId", e.target.value.toUpperCase())
                  }
                  placeholder="TEMP-PARTS-IMMERSIONS"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Code from the datasheets collection (if relevant).
                </p>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensions / notes
                </label>
                <textarea
                  rows={2}
                  value={form.dimensions}
                  onChange={(e) => setField("dimensions", e.target.value)}
                  placeholder='e.g. "L=385mm, 2 1/4\" BSP, copper sheath"'
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Internal description / spec."
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
          </section>

          {/* Image section */}
          <section className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Part image (SharePoint)
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Upload a hero image for this part to <code>/PartImages/{form.partId || "PART_ID"}</code>.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
              <div className="text-gray-700">
                <b>Folder:</b>{" "}
                {form.imageFolderId ? "Linked" : "Not created yet"}
              </div>
              {form.imageFolderUrl && (
                <a
                  href={form.imageFolderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  Open folder
                </a>
              )}
            </div>

            <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image file
                </label>
                <input
                  type="file"
                  accept="image/*"
                  disabled={!form.partId.trim()}
                  onChange={(e) =>
                    setImageFile(e.target.files?.[0] || null)
                  }
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                />
                {form.imageUrl && (
                  <div className="mt-2 flex items-center gap-3">
                    <a
                      href={form.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 text-sm"
                    >
                      View current image
                    </a>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={uploadImage}
                disabled={!imageFile || uploadingImage}
                className="px-4 py-2 rounded-md border border-gray-300 bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-60"
              >
                {uploadingImage ? "Uploading…" : "Upload image"}
              </button>
            </div>
          </section>

          {/* Spacer for sticky bar */}
          <div className="h-16" />

          {/* Sticky save bar */}
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {selectedId
                  ? `Selected: ${selectedId}`
                  : mode === "create"
                  ? "Creating new part"
                  : "No part selected"}
                {form.partId && ` · Part ID ${form.partId}`}
                {form.supplier && ` · ${form.supplier}`}
              </span>

              {infoMessage && (
                <span className="text-sm text-green-600">{infoMessage}</span>
              )}
              {saveError && (
                <span className="text-sm text-red-600">{saveError}</span>
              )}

              <button
                type="button"
                onClick={startNew}
                className="ml-auto px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Reset form
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-6 py-2 rounded-md text-white text-sm transition ${
                  isSaving
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSaving ? "Saving…" : "Save part"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
